<template>
  <div class="space-y-2">
    <div class="flex items-center justify-between gap-2">
      <div class="text-xs text-gray-500 dark:text-gray-400">
        <span v-if="props.mapping?.bacnetObjectType"
          >{{ tt('Expected object: ', 'Pričakovani objekt: ') }}{{ props.mapping.bacnetObjectType }}</span
        >
        <span v-else>{{ tt('Select a BACnet object', 'Izberite objekt BACnet') }}</span>
        <span v-if="props.mapping?.bacnetPropertyId"> • {{ props.mapping.bacnetPropertyId }}</span>
      </div>
      <button
        v-if="localConfig.propertyId"
        type="button"
        class="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        @click="clearSelection">
        {{ tt('Clear', 'Počisti') }}
      </button>
    </div>

    <select
      v-model="selectedPropertyId"
      class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      :disabled="loading"
      @change="onSelectionChange">
      <option value="">{{ tt('Select BACnet object...', 'Izberite objekt BACnet...') }}</option>
      <option
        v-for="bacnetObject in filteredObjects"
        :key="bacnetObject.bacnet_object_id"
        :value="bacnetObject.bacnet_object_id">
        {{ formatObjectLabel(bacnetObject) }}
      </option>
    </select>

    <div v-if="error" class="text-xs text-red-600 dark:text-red-400">
      {{ error }}
    </div>
    <div v-else-if="loading" class="text-xs text-gray-500 dark:text-gray-400">
      {{ tt('Loading BACnet objects...', 'Nalaganje objektov BACnet...') }}
    </div>
    <div v-else-if="!filteredObjects.length" class="text-xs text-gray-500 dark:text-gray-400 italic">
      {{ tt('No compatible BACnet objects available.', 'Ni na voljo združljivih objektov BACnet.') }}
    </div>

    <div v-if="localConfig.propertyId" class="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-300">
      <div>
        <span class="font-medium">{{ tt('Device:', 'Naprava:') }}</span>
        {{ localConfig.deviceInstance }}
      </div>
      <div>
        <span class="font-medium">{{ tt('Object:', 'Objekt:') }}</span>
        {{ localConfig.objectType }}:{{ localConfig.objectInstance }}
      </div>
      <div>
        <span class="font-medium">{{ tt('Property:', 'Lastnost:') }}</span>
        {{ localConfig.propertyName || 'PRESENT_VALUE' }}
      </div>
      <div>
        <span class="font-medium">{{ tt('Address:', 'Naslov:') }}</span>
        {{ localConfig.address || '-' }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import type { PropertyMapping } from '../shared/propertyMapping';
import { apiFetch } from '../services/api';
import { tt } from '../utils/consoleText';

interface BacnetObjectOption {
  bacnet_object_id: string;
  device_instance: number;
  object_type: string;
  object_instance: number;
  propertyId: string;
  object_name?: string;
  bacnetDevice?: {
    id?: string;
    deviceId: number;
    address: string;
    deviceName?: string | null;
  };
}

export interface BacnetConfig {
  propertyId: string;
  bacnetDeviceId?: string;
  deviceInstance: number;
  address: string;
  objectType: string;
  objectInstance: number;
  propertyName?: string;
  objectName?: string;
}

interface Props {
  modelValue?: BacnetConfig;
  mapping: PropertyMapping | undefined;
  integrationId: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: BacnetConfig];
}>();

const loading = ref(false);
const error = ref<string | null>(null);
const bacnetObjects = ref<BacnetObjectOption[]>([]);

const localConfig = ref<BacnetConfig>({
  propertyId: props.modelValue?.propertyId || '',
  bacnetDeviceId: props.modelValue?.bacnetDeviceId,
  deviceInstance: props.modelValue?.deviceInstance ?? 0,
  address: props.modelValue?.address || '',
  objectType: props.modelValue?.objectType || props.mapping?.bacnetObjectType || '',
  objectInstance: props.modelValue?.objectInstance ?? 0,
  propertyName: props.modelValue?.propertyName || String(props.mapping?.bacnetPropertyId || 'PRESENT_VALUE'),
  objectName: props.modelValue?.objectName,
});

const selectedPropertyId = ref(localConfig.value.propertyId);

const filteredObjects = computed(() => {
  return bacnetObjects.value.filter((bacnetObject) => {
    if (props.mapping?.bacnetObjectType && bacnetObject.object_type !== props.mapping.bacnetObjectType) {
      return false;
    }

    if (props.mapping?.bacnetPropertyId && bacnetObject.propertyId !== String(props.mapping.bacnetPropertyId)) {
      return false;
    }

    return true;
  });
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) {
      return;
    }

    localConfig.value = { ...newValue };
    selectedPropertyId.value = newValue.propertyId || '';
  },
  { deep: true },
);

watch(
  () => props.integrationId,
  async () => {
    await loadBacnetObjects();
  },
);

onMounted(async () => {
  await loadBacnetObjects();
});

function formatObjectLabel(bacnetObject: BacnetObjectOption): string {
  const deviceName = bacnetObject.bacnetDevice?.deviceName ? `${bacnetObject.bacnetDevice.deviceName} ` : '';
  const objectName = bacnetObject.object_name ? ` - ${bacnetObject.object_name}` : '';
  return `${deviceName}(${bacnetObject.device_instance}) ${bacnetObject.object_type}:${bacnetObject.object_instance}${objectName}`;
}

function clearSelection() {
  localConfig.value = {
    propertyId: '',
    bacnetDeviceId: undefined,
    deviceInstance: 0,
    address: '',
    objectType: props.mapping?.bacnetObjectType || '',
    objectInstance: 0,
    propertyName: String(props.mapping?.bacnetPropertyId || 'PRESENT_VALUE'),
    objectName: undefined,
  };
  selectedPropertyId.value = '';
  emit('update:modelValue', { ...localConfig.value });
}

function onSelectionChange() {
  const bacnetObject = bacnetObjects.value.find((item) => item.bacnet_object_id === selectedPropertyId.value);

  if (!bacnetObject) {
    clearSelection();
    return;
  }

  localConfig.value = {
    propertyId: bacnetObject.bacnet_object_id,
    bacnetDeviceId: bacnetObject.bacnetDevice?.id,
    deviceInstance: bacnetObject.bacnetDevice?.deviceId ?? bacnetObject.device_instance,
    address: bacnetObject.bacnetDevice?.address || '',
    objectType: bacnetObject.object_type,
    objectInstance: bacnetObject.object_instance,
    propertyName: bacnetObject.propertyId,
    objectName: bacnetObject.object_name,
  };

  emit('update:modelValue', { ...localConfig.value });
}

async function loadBacnetObjects() {
  if (!props.integrationId) {
    bacnetObjects.value = [];
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    const data = await apiFetch(`/integrations/${props.integrationId}/datapoints`);
    bacnetObjects.value = data.bacnet || [];
  } catch (err) {
    console.error('[BacnetReferenceInput] Failed to load BACnet objects', err);
    error.value =
      err instanceof Error
        ? err.message
        : tt('Failed to load BACnet objects', 'Objektov BACnet ni bilo mogoče naložiti');
  } finally {
    loading.value = false;
  }
}
</script>
